import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TuiLetModule, tuiPure } from '@taiga-ui/cdk';
import { TuiButtonModule, TuiDialogModule, TuiSvgModule } from '@taiga-ui/core';
import { ENVIRONMENT } from '@us/app-config';
import {
    Cart,
    CartActions,
    CartItem,
    CartPageStore,
    selectCart,
    selectLoading,
    selectShowAddItemButton
} from '@us/web/cart/data-access';
import { OrderActionsComponent, OrderItemsComponent } from '@us/web/orders/feature';
// import { PromotionSelectComponent } from '@us/web/promotions/feature';
import { AppType, BusinessModel, OrderItem, OrderState, OrderType } from '@us/web/shared/data-access';
import { FormatCurrencyPipe, selectPaymentNotes } from '@us/web/shop/data-access';
import { Observable } from 'rxjs';
import { CartCheckoutBtnComponent } from '../cart-checkout-btn/cart-checkout-btn.component';
import { RouteService } from '@us/web/shops/feature';
import { BreadcrumbsComponent } from '@us/web/shared/ui';
import { LetDirective } from '@ngrx/component';
import { PromotionSelectComponent } from '@us/web/promotions/feature';
import { isFnBApp } from '@us/web/shared/utils';
import { selectUser } from '@us/web/auth/data-access';
import { TuiTextAreaModule } from '@taiga-ui/kit';

export type QuantityChangedOutputType = { item: OrderItem; quantity: number };

@UntilDestroy()
@Component({
    selector: 'us-cart',
    templateUrl: './cart.component.html',
    styleUrls: ['./cart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        NgClass,
        NgIf,
        AsyncPipe,
        TranslocoModule,
        FormatCurrencyPipe,
        RouterLink,
        TuiButtonModule,
        TuiLetModule,
        NgForOf,
        OrderActionsComponent,
        OrderItemsComponent,
        ReactiveFormsModule,
        CartCheckoutBtnComponent,
        BreadcrumbsComponent,
        LetDirective,
        PromotionSelectComponent,
        TuiSvgModule,
        FormsModule,
        ReactiveFormsModule,
        TuiTextAreaModule,
        TuiDialogModule
    ]
})
export class CartComponent {
    private readonly store = inject(Store);
    private readonly env = inject(ENVIRONMENT);
    protected readonly routeService = inject(RouteService);
    private readonly cartStore = inject(CartPageStore);

    readonly isFnb = isFnBApp(this.env);

    @Input() title?: string;
    @Input() className = '';

    readonly homePageUrl$ = this.routeService.homePageUrl$;
    readonly cart$: Observable<Cart> = this.store.select(selectCart);
    readonly loading$: Observable<boolean> = this.store.select(selectLoading);
    // readonly totalNewItems$: Observable<number> = this.store.select(selectCartTotalNewItems);
    readonly isCartOwner = this.cartStore.isCartOwner;

    readonly paymentNotes$ = this.store.select(selectPaymentNotes);
    readonly OrderState = OrderState;
    readonly OrderType = OrderType;
    readonly appType: AppType;
    readonly showAddItemButton = this.store.selectSignal(selectShowAddItemButton);
    readonly user$ = this.store.select(selectUser);
    openNote = false;
    noteData = '';

    constructor() {
        this.appType = this.env.appType as AppType;
        this.cart$.subscribe(cart => {
            this.noteData = cart.note;
        });
    }

    @tuiPure
    isPayLater(businessModel: BusinessModel): boolean {
        return businessModel === BusinessModel.PayLater;
    }

    onChangeItemQuantity(event: QuantityChangedOutputType) {
        const { item, quantity } = event;

        if (quantity === 0) {
            this.onRemoveItemFromCart(item);
            return;
        }

        this.store.dispatch(CartActions.updateItemQuantity({ item, quantity }));
    }

    onRemoveItemFromCart(item: CartItem) {
        this.store.dispatch(CartActions.removeFromCart({ item }));
    }

    @tuiPure
    isRetail() {
        return this.appType === AppType.Retail;
    }

    saveNote() {
        this.store.dispatch(CartActions.updateCart({ note: this.noteData }));
        this.openNote = false;
    }
}
